import React from 'react';
import { useAssessmentData } from './AssessmentDataContext';
import { SectionHeader, WinRateTable } from './shared';

// Extracted verbatim from Assessment.js's `activeTab === 'win_rate'` render
// block (Tech Debt 2026-09, "Assessment and Backtest are god files" --
// remaining-7-tabs context lift). Reads shared state from
// useAssessmentData() instead of via props. The toolbar's Scaled/Unscaled
// toggle button stays in Assessment.js (shared toolbar chrome, not tab
// content) -- this component only reads `showScaled`.

export default function WinRateTab() {
  const { selectedWindow, showScaled, winRateBuckets, prevByBucket, dteCfg } = useAssessmentData();
  return (
  <>
    <SectionHeader>
      Win Rate by bucket × period{selectedWindow ? ` — ${selectedWindow} window` : ''}
    </SectionHeader>
    <div className="mb-4 space-y-2">
      <p className="text-[12px] text-gray-400 leading-relaxed">
        Win rates use <span className="text-gray-200 font-medium">volatility-adjusted barriers</span> — each trade is measured against targets scaled to that stock's own 60-day realized volatility (σ).
        Toggle between <span className="text-gray-200 font-medium">Scaled</span> and <span className="text-amber-300 font-medium">Unscaled</span> using the button in the toolbar.
      </p>
      <div className="bg-trading-dark-900 border border-white/[0.06] rounded-md px-4 py-3 font-mono text-[11px] text-gray-400 space-y-2 tabular-nums">
        {showScaled ? (
          <>
            <div className="text-[10px] font-medium uppercase tracking-[0.14em] text-gray-300 font-sans">Scaled — barrier grows with window: K·σ·√(W/30)</div>
            <div className="flex flex-col gap-0.5">
              <span className="text-trading-green-300">Calls</span>
              <span>win if high ≥ <span className="text-gray-100">entry × (1 + 2σ·√(W/30))</span></span>
              <span className="text-gray-600">stop if low ≤ <span className="text-gray-400">entry × (1 − 5σ·√(W/30))</span></span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-trading-red-300">Puts</span>
              <span>win if low ≤ <span className="text-gray-100">entry × (1 − 1σ·√(W/30))</span></span>
              <span className="text-gray-600">stop if high ≥ <span className="text-gray-400">entry × (1 + 2σ·√(W/30))</span></span>
            </div>
            <div className="text-gray-500 pt-1 border-t border-white/[0.06] leading-relaxed">
              <span className="text-gray-400">Scale √(W/30):</span>
              {' '}3d ≈ 0.32×{' · '}5d ≈ 0.41×{' · '}7d ≈ 0.48×{' · '}15d ≈ 0.71×{' · '}30d = 1.0× (anchor){' · '}60d ≈ 1.41×{' · '}90d ≈ 1.73×
              <br />1d = direction only (no barrier)
            </div>
          </>
        ) : (
          <>
            <div className="text-[10px] font-medium uppercase tracking-[0.14em] text-amber-300 font-sans">Unscaled — fixed 30d barrier for all windows: K·σ</div>
            <div className="flex flex-col gap-0.5">
              <span className="text-trading-green-300">Calls</span>
              <span>win if high ≥ <span className="text-gray-100">entry × (1 + 2σ)</span></span>
              <span className="text-gray-600">stop if low ≤ <span className="text-gray-400">entry × (1 − 5σ)</span></span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-trading-red-300">Puts</span>
              <span>win if low ≤ <span className="text-gray-100">entry × (1 − 1σ)</span></span>
              <span className="text-gray-600">stop if high ≥ <span className="text-gray-400">entry × (1 + 2σ)</span></span>
            </div>
            <div className="text-gray-500 pt-1 border-t border-white/[0.06] leading-relaxed">
              Same absolute target at every window — shorter periods are harder to reach, longer periods are easier.
              <br />1d = direction only (no barrier)
            </div>
          </>
        )}
      </div>
    </div>
    {winRateBuckets.length === 0
      ? <p className="text-gray-500 text-sm text-center py-8">No buckets to display.</p>
      : <WinRateTable buckets={winRateBuckets} useUnscaled={!showScaled}
          prevByBucket={prevByBucket} primaryPeriods={dteCfg.primaries} />
    }
  </>
  );
}
